import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, Plus, Save, Building2, Plane, Bus, FileText } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import QuoteLineItemEditor, {
  LineItemDraft,
  QuoteLineItemType,
} from '../../components/admin/QuoteLineItemEditor';
import { useAuth } from '../../lib/auth';
import { GET_QUOTE } from '../../graphql/queries/quote';
import { CREATE_QUOTE, UPDATE_QUOTE } from '../../graphql/mutations/quote';
import { GET_CLIENTS, GET_AGENCY_BY_NAME } from '../../graphql/admin/queries/client';

interface ClientOption {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

function defaultValidUntil() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function makeNewLineItem(type: QuoteLineItemType, position: number): LineItemDraft {
  return {
    type,
    position,
    title: '',
    quantity: 1,
    unitPrice: 0,
    total: 0,
  };
}

export default function QuoteFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const userAgencyId = (user as any)?.agencyId || (user as any)?.agency?.id;
  const agencyName = (user as any)?.agencyName || (user as any)?.agency?.name;

  // Fallback: si agencyId manque en localStorage (anciens logins), le récupérer
  // via le nom de l'agence.
  const { data: agencyLookup } = useQuery(GET_AGENCY_BY_NAME, {
    variables: { name: agencyName },
    skip: Boolean(userAgencyId) || !agencyName,
  });
  const agencyId = userAgencyId || agencyLookup?.agency?.id;

  // Données existantes (edit)
  const { data: quoteData, loading: loadingQuote } = useQuery(GET_QUOTE, {
    variables: { id: Number(id) },
    skip: !isEdit,
    fetchPolicy: 'network-only',
  });

  // Liste des clients
  const { data: clientsData } = useQuery(GET_CLIENTS, {
    variables: { agencyId: Number(agencyId), agencyName },
    skip: !agencyId,
  });
  const clients: ClientOption[] = clientsData?.clients || [];

  // ─── State du formulaire ──────────────────────────────────────────────────
  const [clientId, setClientId] = useState<number | null>(null);
  const [contactNom, setContactNom] = useState('');
  const [contactPrenom, setContactPrenom] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTelephone, setContactTelephone] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [destination, setDestination] = useState('');
  const [travelDateStart, setTravelDateStart] = useState('');
  const [travelDateEnd, setTravelDateEnd] = useState('');
  const [validUntil, setValidUntil] = useState(defaultValidUntil());
  const [nbrAdulte, setNbrAdulte] = useState(1);
  const [nbrEnfant, setNbrEnfant] = useState(0);
  const [nbrBebe, setNbrBebe] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [currency, setCurrency] = useState('DZD');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Pré-remplir en mode édition
  useEffect(() => {
    if (!isEdit || !quoteData?.quote) return;
    const q = quoteData.quote;
    setClientId(q.clientId);
    setContactNom(q.contactNom || '');
    setContactPrenom(q.contactPrenom || '');
    setContactEmail(q.contactEmail || '');
    setContactTelephone(q.contactTelephone || '');
    setTitle(q.title || '');
    setDescription(q.description || '');
    setDestination(q.destination || '');
    setTravelDateStart(q.travelDateStart ? q.travelDateStart.slice(0, 10) : '');
    setTravelDateEnd(q.travelDateEnd ? q.travelDateEnd.slice(0, 10) : '');
    setValidUntil(q.validUntil ? q.validUntil.slice(0, 10) : defaultValidUntil());
    setNbrAdulte(q.nbrAdulte ?? 1);
    setNbrEnfant(q.nbrEnfant ?? 0);
    setNbrBebe(q.nbrBebe ?? 0);
    setDiscountPercent(q.discountPercent ?? 0);
    setTaxPercent(q.taxPercent ?? 0);
    setCurrency(q.currency || 'DZD');
    setNotes(q.notes || '');
    setLineItems(
      (q.lineItems || []).map((li: any, idx: number) => ({
        id: li.id,
        type: li.type,
        position: li.position ?? idx,
        title: li.title || '',
        description: li.description || '',
        quantity: li.quantity ?? 1,
        unitPrice: li.unitPrice ?? 0,
        total: li.total ?? 0,
        hotelName: li.hotelName,
        hotelLocation: li.hotelLocation,
        hotelStars: li.hotelStars,
        checkIn: li.checkIn,
        checkOut: li.checkOut,
        numberOfNights: li.numberOfNights,
        numberOfRooms: li.numberOfRooms,
        roomType: li.roomType,
        mealPlan: li.mealPlan,
        airline: li.airline,
        flightFrom: li.flightFrom,
        flightTo: li.flightTo,
        departureDate: li.departureDate,
        returnDate: li.returnDate,
        flightClass: li.flightClass,
        flightNumber: li.flightNumber,
        transferFrom: li.transferFrom,
        transferTo: li.transferTo,
        transferDate: li.transferDate,
        vehicleType: li.vehicleType,
        providerSource: li.providerSource || 'manual',
        providerRefId: li.providerRefId,
        providerData: li.providerData,
      }))
    );
  }, [isEdit, quoteData]);

  // Quand on choisit un client, pré-remplir les champs contact
  const handleSelectClient = (cid: number | null) => {
    setClientId(cid);
    if (!cid) return;
    const c = clients.find((x) => x.id === cid);
    if (c) {
      setContactNom(c.lastName);
      setContactPrenom(c.firstName);
      setContactEmail(c.email);
      setContactTelephone(c.phone);
    }
  };

  // ─── Totaux dérivés ──────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((sum, it) => {
      const t = it.total ?? (it.quantity || 1) * (it.unitPrice || 0);
      return sum + (Number(t) || 0);
    }, 0);
    const discountAmount = (subtotal * (Number(discountPercent) || 0)) / 100;
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = (afterDiscount * (Number(taxPercent) || 0)) / 100;
    const totalPrice = afterDiscount + taxAmount;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      totalPrice: Math.round(totalPrice * 100) / 100,
    };
  }, [lineItems, discountPercent, taxPercent]);

  // ─── Mutations ───────────────────────────────────────────────────────────
  const [createQuote] = useMutation(CREATE_QUOTE);
  const [updateQuote] = useMutation(UPDATE_QUOTE);

  const addLineItem = (type: QuoteLineItemType) => {
    setLineItems((prev) => [...prev, makeNewLineItem(type, prev.length)]);
  };

  const updateLineItem = (idx: number, next: LineItemDraft) => {
    setLineItems((prev) => prev.map((it, i) => (i === idx ? next : it)));
  };

  const removeLineItem = (idx: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, position: i })));
  };

  const handleSubmit = async (status: 'DRAFT' | 'SENT' = 'DRAFT') => {
    setFormError(null);

    if (!agencyId) {
      setFormError('Agence introuvable. Veuillez vous reconnecter.');
      return;
    }
    if (!title.trim()) {
      setFormError('Le titre du devis est requis.');
      return;
    }
    if (!contactNom.trim() || !contactPrenom.trim()) {
      setFormError('Le nom et prénom du contact sont requis.');
      return;
    }
    if (!contactEmail.trim()) {
      setFormError("L'email du contact est requis.");
      return;
    }
    if (lineItems.length === 0) {
      setFormError('Ajoutez au moins une prestation au devis.');
      return;
    }

    const input = {
      status,
      clientId: clientId ?? null,
      contactNom: contactNom.trim(),
      contactPrenom: contactPrenom.trim(),
      contactEmail: contactEmail.trim(),
      contactTelephone: contactTelephone.trim(),
      title: title.trim(),
      description: description || null,
      destination: destination || null,
      travelDateStart: travelDateStart || null,
      travelDateEnd: travelDateEnd || null,
      validUntil: validUntil || defaultValidUntil(),
      nbrAdulte: Number(nbrAdulte) || 1,
      nbrEnfant: Number(nbrEnfant) || 0,
      nbrBebe: Number(nbrBebe) || 0,
      discountPercent: Number(discountPercent) || 0,
      taxPercent: Number(taxPercent) || 0,
      currency,
      agencyId: Number(agencyId),
      notes: notes || null,
      lineItems: lineItems.map((it, idx) => ({
        type: it.type,
        position: idx,
        title: it.title,
        description: it.description || null,
        quantity: Number(it.quantity) || 1,
        unitPrice: Number(it.unitPrice) || 0,
        total: Number(it.total) || 0,
        hotelName: it.hotelName || null,
        hotelLocation: it.hotelLocation || null,
        hotelStars: it.hotelStars || null,
        checkIn: it.checkIn || null,
        checkOut: it.checkOut || null,
        numberOfNights: it.numberOfNights ?? null,
        numberOfRooms: it.numberOfRooms ?? null,
        roomType: it.roomType || null,
        mealPlan: it.mealPlan || null,
        airline: it.airline || null,
        flightFrom: it.flightFrom || null,
        flightTo: it.flightTo || null,
        departureDate: it.departureDate || null,
        returnDate: it.returnDate || null,
        flightClass: it.flightClass || null,
        flightNumber: it.flightNumber || null,
        transferFrom: it.transferFrom || null,
        transferTo: it.transferTo || null,
        transferDate: it.transferDate || null,
        vehicleType: it.vehicleType || null,
        providerSource: it.providerSource || 'manual',
        providerRefId: it.providerRefId || null,
        providerData: it.providerData || null,
      })),
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        const res = await updateQuote({ variables: { id: Number(id), input } });
        const qid = res.data?.updateQuote?.id;
        navigate(`/admin/quotes/${qid || id}`);
      } else {
        const res = await createQuote({ variables: { input } });
        const qid = res.data?.createQuote?.id;
        if (qid) navigate(`/admin/quotes/${qid}`);
        else navigate('/admin/quotes');
      }
    } catch (e: any) {
      setFormError(e.message || 'Erreur lors de la sauvegarde du devis.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && loadingQuote) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-gray-500">Chargement du devis...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/quotes')}
            className="text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold">
            {isEdit ? 'Modifier le devis' : 'Nouveau devis'}
          </h1>
        </div>

        {formError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
            {formError}
          </div>
        )}

        {/* Bloc Contact */}
        <Section title="Contact / Client">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Client existant (optionnel)</Label>
              <select
                value={clientId ?? ''}
                onChange={(e) =>
                  handleSelectClient(e.target.value ? Number(e.target.value) : null)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">— Saisir manuellement —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName} ({c.email})
                  </option>
                ))}
              </select>
            </div>
            <div />
            <Input label="Nom" value={contactNom} onChange={setContactNom} required />
            <Input label="Prénom" value={contactPrenom} onChange={setContactPrenom} required />
            <Input label="Email" type="email" value={contactEmail} onChange={setContactEmail} required />
            <Input label="Téléphone" value={contactTelephone} onChange={setContactTelephone} />
          </div>
        </Section>

        {/* Bloc Voyage */}
        <Section title="Détails du voyage">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Titre du devis" value={title} onChange={setTitle} required full />
            <Input label="Destination" value={destination} onChange={setDestination} />
            <Input label="Date de validité du devis" type="date" value={validUntil} onChange={setValidUntil} />
            <Input label="Date de départ prévue" type="date" value={travelDateStart} onChange={setTravelDateStart} />
            <Input label="Date de retour prévue" type="date" value={travelDateEnd} onChange={setTravelDateEnd} />
            <div className="md:col-span-2">
              <Label>Description / Programme</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Court résumé du voyage (visible par le client)"
              />
            </div>
            <Input label="Nombre d'adultes" type="number" value={nbrAdulte} onChange={(v) => setNbrAdulte(Number(v) || 1)} />
            <Input label="Nombre d'enfants" type="number" value={nbrEnfant} onChange={(v) => setNbrEnfant(Number(v) || 0)} />
            <Input label="Nombre de bébés" type="number" value={nbrBebe} onChange={(v) => setNbrBebe(Number(v) || 0)} />
          </div>
        </Section>

        {/* Bloc Prestations */}
        <Section
          title="Prestations"
          subtitle="Ajoutez les lignes du devis (hôtel, vol, transfert, ou prestation libre)."
        >
          <div className="space-y-4">
            {lineItems.map((it, idx) => (
              <QuoteLineItemEditor
                key={it.id ?? `tmp-${idx}`}
                item={it}
                onChange={(next) => updateLineItem(idx, next)}
                onRemove={() => removeLineItem(idx)}
              />
            ))}
            {lineItems.length === 0 && (
              <div className="text-sm text-gray-500 italic">
                Aucune prestation pour l'instant. Ajoutez-en une via les boutons ci-dessous.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <AddBtn icon={Building2} label="Hôtel" onClick={() => addLineItem('HOTEL')} />
            <AddBtn icon={Plane} label="Vol" onClick={() => addLineItem('FLIGHT')} />
            <AddBtn icon={Bus} label="Transfert" onClick={() => addLineItem('TRANSFER')} />
            <AddBtn icon={FileText} label="Prestation libre" onClick={() => addLineItem('CUSTOM')} />
          </div>
        </Section>

        {/* Bloc Totaux */}
        <Section title="Totaux">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Input
                label="Remise (%)"
                type="number"
                value={discountPercent}
                onChange={(v) => setDiscountPercent(Math.max(0, Number(v) || 0))}
              />
              <Input
                label="TVA / Taxes (%)"
                type="number"
                value={taxPercent}
                onChange={(v) => setTaxPercent(Math.max(0, Number(v) || 0))}
              />
              <div>
                <Label>Devise</Label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="DZD">DZD - Dinar algérien</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - Dollar</option>
                  <option value="MAD">MAD - Dirham marocain</option>
                  <option value="TND">TND - Dinar tunisien</option>
                </select>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <Row label="Sous-total" value={totals.subtotal} currency={currency} />
              <Row
                label={`Remise (${discountPercent}%)`}
                value={-totals.discountAmount}
                currency={currency}
                negative
              />
              <Row label={`Taxes (${taxPercent}%)`} value={totals.taxAmount} currency={currency} />
              <div className="border-t border-gray-300 pt-2 mt-2">
                <Row label="TOTAL" value={totals.totalPrice} currency={currency} bold />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Label>Notes internes (non visibles par le client)</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </Section>

        {/* Boutons d'action */}
        <div className="flex flex-wrap gap-3 justify-end pb-8">
          <button
            type="button"
            onClick={() => navigate('/admin/quotes')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleSubmit('DRAFT')}
            className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Enregistrer en brouillon
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleSubmit('SENT')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isEdit ? 'Enregistrer & envoyer' : 'Créer & envoyer'}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}

// ─── Petits helpers UI ──────────────────────────────────────────────────────

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-lg shadow p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-sm font-medium text-gray-700 mb-1">{children}</label>;
}

interface InputProps {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  full?: boolean;
}

function Input({ label, value, onChange, type = 'text', required, full }: InputProps) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <Label>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </Label>
      <input
        type={type}
        value={value as any}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    </div>
  );
}

function AddBtn({ icon: Icon, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:border-blue-400"
    >
      <Plus className="w-4 h-4" />
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function Row({
  label,
  value,
  currency,
  negative,
  bold,
}: {
  label: string;
  value: number;
  currency: string;
  negative?: boolean;
  bold?: boolean;
}) {
  const formatted = (() => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${value.toLocaleString('fr-FR')} ${currency}`;
    }
  })();
  return (
    <div className={`flex items-center justify-between ${bold ? 'text-base font-bold' : 'text-sm'}`}>
      <span className={negative ? 'text-red-600' : ''}>{label}</span>
      <span className={negative ? 'text-red-600' : ''}>{formatted}</span>
    </div>
  );
}
